






































































import DateRangeControl, {DateRange} from '@/components/common/DateRangeControl.vue';
import EstateTypeControl from '@/components/common/EstateTypeControl.vue';
import LocationSearchControl from '@/components/common/LocationSearchControl.vue';
import {DATE_FORMAT} from '@/components/DatePicker';
import {canvasMarker} from '@/components/maps/CanvasMarker';
import LeftPanel from '@/components/maps/LeftPanel.vue';
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {EstateType, Location} from '@/model/api';
import {log} from '@/model/logger';
import {concat, NonEmptyArray} from 'fp-ts/es6/NonEmptyArray';
import L from 'leaflet';
import moment from 'moment/moment';
import naja from 'naja';
import {Component, Prop, Ref, Vue, Watch} from 'vue-property-decorator';
import {LCircleMarker, LControl, LControlLayers, LControlZoom, LMap, LMarker, LPopup} from 'vue2-leaflet';

interface Point {
    readonly deduplicationKey: string | null;
    readonly coordinates: {
        readonly latitude: number;
        readonly longitude: number;
    };
    readonly name: string;
    readonly link: string;
    readonly isLinkAjax: boolean;
    readonly color: string;
}

interface Marker {
    readonly colors: NonEmptyArray<string>;
    readonly points: NonEmptyArray<Point>;
    readonly coordinates: L.LatLngTuple;
}

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

interface LegendItem {
    readonly label: string;
    readonly color: string;
}

type Color = string;

@Component({
    components: {
        MapyCzOrthoPhotoLayer,
        MapyCzBasicLayer,
        LocationSearchControl,
        LeftPanel,
        LPopup,
        LMap,
        LMarker,
        LCircleMarker,
        LControl,
        LControlLayers,
        LControlZoom,
        DateRangeControl,
        EstateTypeControl,
    },
})
export default class PointsMap extends Vue {
    protected get centerLatLng(): L.LatLng {
        return L.latLng(this.center.latitude, this.center.longitude);
    }

    @Prop({type: Object, required: true})
    public readonly center: Coordinates;

    @Prop({type: String, required: true})
    public readonly providerUrl: string;

    @Prop({type: Boolean, default: false})
    public readonly searchControl: boolean;

    @Prop({type: Array, required: true})
    public readonly legendItems: LegendItem[];

    @Prop({type: Array, default: true})
    public readonly estateTypes: EstateType[];

    @Prop({type: Object})
    public readonly defaultDateRange: DateRange;

    @Ref()
    protected readonly map: LMap;

    protected dateRange: DateRange;

    protected estateType: EstateType | null = null;

    protected static MARKER_RADIUS = 10;
    private static MIN_ZOOM = 14;
    private static DEFAULT_ZOOM = 16;
    private static SEARCH_ZOOM = 20;

    private zoom = PointsMap.DEFAULT_ZOOM;

    private loading = false;

    protected activeMarker: Marker | null = null;

    private previousBounds: L.LatLngBounds | null;
    private previousAbortController: AbortController | null = null;
    private previousMarkers: Map<string, [Marker, L.Path]> = new Map();

    private markerIconTemplates: Map<number, Promise<string>> = new Map();
    private markerIconTemplatePlaceholders = ['red', 'green', 'blue'];

    protected get zoomTooSmall(): boolean {
        return this.zoom < PointsMap.MIN_ZOOM;
    }

    public created() {
        this.dateRange = this.defaultDateRange;
    }

    public mounted() {
        this.tryRefreshMarkers();
    }

    public updateCenterPoint(location: Location | null): void {
        if (location === null || location.center === null) {
            return;
        }

        this.map.mapObject.flyTo(
            [location.center.coordinates[1], location.center.coordinates[0]],
            PointsMap.SEARCH_ZOOM,
            {
                duration: 0,
                animate: false,
            },
        );
    }

    protected legendMarkerStyles(color: string): object {
        const size = PointsMap.MARKER_RADIUS * 2 + 'px';

        return {
            height: size,
            width: size,
            backgroundColor: color,
        };
    }

    protected updateZoom(zoom: number): void {
        this.zoom = zoom;
    }

    protected async tryRefreshMarkers(): Promise<void> {
        const abortController = new AbortController();

        try {
            this.loading = true;
            this.previousAbortController?.abort();
            this.previousAbortController = abortController;

            await this.refreshMarkers(abortController.signal);
            this.loading = false;
        } catch (error) {
            if (!abortController.signal.aborted) {
                log(error);
            }
        }
    }

    protected markerClicked(marker: Marker): void {
        if (marker.points.length > 1) {
            this.activeMarker = marker;
            return;
        }

        this.pointClicked(marker.points[0]);
    }

    protected pointClicked(point: Point): void {
        if (point.isLinkAjax) {
            naja.makeRequest('GET', point.link, null, {history: false});
        } else {
            window.open(point.link, '_blank');
        }
    }

    protected dateRangeChange(dateRange: DateRange): void {
        this.clearCache();

        this.dateRange = dateRange;

        this.tryRefreshMarkers();
    }

    protected estateTypeChange(estateType: EstateType | null): void {
        this.clearCache();

        this.estateType = estateType;

        this.tryRefreshMarkers();
    }

    @Watch('providerUrl')
    private clearCache() {
        this.previousBounds = null;
    }

    private async refreshMarkers(signal: AbortSignal): Promise<void> {
        const bounds = this.map.mapObject.getBounds();

        if (this.zoom < PointsMap.MIN_ZOOM) {
            await this.replaceVisibleMarkers(new Map());
            return;
        }

        if (this.previousBounds?.contains(bounds)) {
            // We already have all markers visible
            return;
        }

        const response = await fetch(
            this.providerUrl.replace(
                encodeURIComponent('{bounding_box}'),
                JSON.stringify({
                    southWest: this.latLngToCoordinates(bounds.getSouthWest()),
                    northEast: this.latLngToCoordinates(bounds.getNorthEast()),
                }),
            ).replace(encodeURIComponent('{estate_type}'), JSON.stringify(this.estateType))
                .replace(
                    encodeURIComponent('{date_range}'),
                    JSON.stringify({
                        start: moment(this.dateRange.start).format(DATE_FORMAT),
                        end: moment(this.dateRange.end).format(DATE_FORMAT),
                    })),
            {signal},
        );

        if (!response.ok) {
            throw {
                message: 'Point refresh resulted in error',
                response,
            };
        }

        const points = await response.json() as Point[];
        const markers = this.combineToMarkers(points);

        await this.replaceVisibleMarkers(markers);
    }

    private latLngToCoordinates(coordinates: L.LatLng): Coordinates {
        return {
            longitude: coordinates.lng,
            latitude: coordinates.lat,
        };
    }

    private async replaceVisibleMarkers(markers: Map<string, Marker>): Promise<void> {
        log(`Replacing markers with new dataset of size ${markers.size}`);

        const map = this.map.mapObject;
        const newMarkers = new Map<string, [Marker, L.Path]>();

        let reusedMarkersCount = 0;

        for (const [key, marker] of markers.entries()) {
            const existingMarkerPair = this.previousMarkers.get(key);

            let point: L.Path;

            if (existingMarkerPair) {
                point = existingMarkerPair[1];
                this.previousMarkers.delete(key);
                reusedMarkersCount++;
            } else {
                point = canvasMarker(
                    marker.coordinates,
                    {
                        img: {
                            url: await this.markerIconUrl(marker.colors, marker.points.length),
                            size: [22, 22],
                        },
                    },
                );
                point.addEventListener('click', () => this.markerClicked(marker));
                point.addTo(map);
            }

            newMarkers.set(key, [marker, point]);
        }

        this.previousMarkers.forEach(([, point]) => {
            point.remove();
        });

        log(`${reusedMarkersCount} markers were reused`);

        this.previousMarkers = newMarkers;
    }

    private coordinatesKey(coordinates: L.LatLngTuple): string {
        return `${coordinates[0]}_${coordinates[1]}`;
    }

    private combineToMarkers(points: Point[]): Map<string, Marker> {
        const markers = new Map<string, Marker>();

        points.forEach(point => {
            const coordinates: L.LatLngTuple = [point.coordinates.latitude, point.coordinates.longitude];
            const key = point.deduplicationKey ?? this.coordinatesKey(coordinates);
            const marker = markers.get(key);
            const color = point.color;

            if (!marker) {
                markers.set(
                    key,
                    {
                        coordinates,
                        colors: [color],
                        points: [point],
                    },
                );
            } else {
                markers.set(
                    key,
                    {
                        ...marker,
                        points: concat(marker.points, [point]),
                        colors: marker.colors.includes(color) ? marker.colors : [color, ...marker.colors],
                    },
                );
            }
        });

        return markers;
    }

    private markerIconUrl(colors: NonEmptyArray<string>, count: number): Promise<string> {
        const sortedColors = [...colors].sort();

        return this.markerIconTemplate(sortedColors.length)
            .then(baseTemplate =>
                sortedColors.reduce(
                    (template, color, index) => template
                        .replace(this.markerIconTemplatePlaceholders[index], color)
                    .replace('{count}', count > 1 ? count.toString() : ''),
                    baseTemplate,
                ),
            ).then(svg => `data:image/svg+xml;base64,${btoa(svg)}`);
    }

    private markerIconTemplate(colorCount: number): Promise<string> {
        const cachedTemplate: Promise<string> | undefined = this.markerIconTemplates.get(colorCount);

        if (cachedTemplate !== undefined) {
            return cachedTemplate;
        }

        const promise = fetch(`/images/icons/map/marker-${colorCount}.svg`)
            .then(response => response.text());

        this.markerIconTemplates.set(colorCount, promise);

        return promise;
    }
}
